import React, { useState, useCallback, useEffect } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FaFileAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import Dropzone from '../../components/Dropzone';

export default function CustomerContract({ customer, stateAction, stateInfo }) {
  const routeName = '/customercontracts';
  const dataName = 'Cliente';
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const [customerEdit, setCustomer] = useState(customer);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [contractFile, setContractFile] = useState(false);

  const [formEditContract, setFormEditContract] = useState({
    name: '',
    value: '0.0',
    number: '',
    business_partner: '',
    partner_commission: 0,
    customer_id: customerEdit.id,
  });

  const validationSchemaContract = Yup.object().shape({
    name: Yup.string()
      .typeError('Deve ser um texto')
      .required('Descrição do contrato obrigatória.'),
    value: Yup.number().typeError('Deve ser numérico'),
    number: Yup.number().typeError('Deve ser numérico'),
    status: Yup.number()
      .typeError('Deve ser numérico')
      .required('Status do contrato obrigatório.'),
    business_partner: Yup.string(),
    partner_commission: Yup.number().typeError('Deve ser numérico'),
    date_of_contract: Yup.date().typeError('Deve ser uma data válida'),
  });

  const handleFieldChangeContract = useCallback((fieldName, value) => {
    let newValue = false;

    if (fieldName === 'date_of_contract') {
      let validateDate = moment(value, 'DD/MM/YYYY', true);

      if (!validateDate.isValid()) {
        validateDate = moment(value, 'YYYY-MM-DD', true);
      }

      newValue = validateDate.isValid()
        ? validateDate.format('YYYY-MM-DD 12:00:00')
        : null;
    }

    setFormEditContract((prevValues) => ({
      ...prevValues,
      [fieldName]: newValue === false ? value : newValue,
    }));
  }, []);

  const fieldsContract = [
    {
      name: 'name',
      id: 'name',
      label: 'Descrição',
      type: 'text',
      onChange: (e) => handleFieldChangeContract('name', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'value',
      id: 'value',
      label: 'Valor',
      type: 'number',
      onChange: (e) => handleFieldChangeContract('value', e.target.value),
      colSize: 2,
      additionalProps: {},
    },
    {
      name: 'number',
      id: 'number',
      label: 'Número do contrato',
      type: 'number',
      onChange: (e) => handleFieldChangeContract('number', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
    {
      name: 'contractual_discount_id',
      id: 'contractual_discount_id',
      label: 'Forma de desconto',
      type: 'select',
      options: discounts,
      onChange: (e) =>
        handleFieldChangeContract('contractual_discount_id', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
    {
      name: 'business_partner',
      id: 'business_partner',
      label: 'Parceiro',
      type: 'text',
      onChange: (e) =>
        handleFieldChangeContract('business_partner', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
    {
      name: 'partner_commission',
      id: 'partner_commission',
      label: 'Comissão do parceiro',
      type: 'number',
      onChange: (e) =>
        handleFieldChangeContract('partner_commission', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
    {
      name: 'date_of_contract',
      id: 'date_of_contract',
      label: 'Data da contratação',
      type: 'date',
      onChange: (e) =>
        handleFieldChangeContract('date_of_contract', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
    {
      name: 'status',
      id: 'status',
      label: 'Situação contratual',
      type: 'select',
      options: [
        { value: '', label: '-- Selecione --' },
        { value: 1, label: 'Adimplente' },
        { value: 0, label: 'Inadimplente' },
      ],
      onChange: (e) => handleFieldChangeContract('status', e.target.value),
      colSize: 3,
      additionalProps: {},
    },
  ];

  const getCustomer = useCallback(async () => {
    try {
      const request = await axios.get(`customers/${customerEdit.id}`);

      if (request.data.CustomerContracts.length > 0) {
        request.data.CustomerContracts[0].date_of_contract = moment
          .utc(request.data.CustomerContracts[0].date_of_contract)
          .format('YYYY-MM-DD');
        setFormEditContract({ ...request.data.CustomerContracts[0] });
      }
      setCustomer(request.data);
    } catch (err) {
      setApiErrors(err);
    } finally {
      stateAction(false);
    }
  }, [customerEdit]);

  const getDiscounts = useCallback(async () => {
    try {
      const optionsDiscounts = [{ value: '', label: '-- Selecione --' }];
      const requestDiscounts = await axios.get(`/discounts`);

      if (requestDiscounts) {
        requestDiscounts.data.forEach((data) => {
          if (data.company_id === customerEdit.company_id) {
            optionsDiscounts.push({
              value: data.id,
              label: data.name,
            });
          }
        });
      }

      setDiscounts(optionsDiscounts);
    } catch (err) {
      setApiErrors(err);
    }
  }, [discounts, customerEdit]);

  const handleSubmitContract = async () => {
    try {
      setIsLoading(true);
      let action = 'atualizados';
      if (formEditContract.id) {
        await axios.put(
          `${routeName}/${formEditContract.id}`,
          formEditContract
        );
      } else {
        await axios.post(routeName, formEditContract);
        action = 'cadastrados';
      }

      notification(`Dados contratuais ${action} com sucesso`, 'success');
      // await getCustomer();
      stateAction(true);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFile = async (e) => {
    e.preventDefault();

    if (!contractFile) {
      notification('Nenhuma arquivo informado');
      return;
    }

    const formData = new FormData();
    formData.append('contract_file', contractFile);
    try {
      setIsLoading(true);
      await axios.post(
        `${routeName}/uploadfile/${formEditContract.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      notification('Contrato carregado com sucesso', 'success');
      getCustomer();
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const newFile = e[0];
    setContractFile(newFile);
  };

  useEffect(() => {
    if (customerEdit.CustomerContracts.length > 0) {
      customerEdit.CustomerContracts[0].date_of_contract = moment
        .utc(customerEdit.CustomerContracts[0].date_of_contract)
        .format('YYYY-MM-DD');
      setFormEditContract({ ...customerEdit.CustomerContracts[0] });
    }

    if (stateInfo === true) {
      getCustomer();
    }

    getDiscounts();

    setDataLoaded(true);
  }, [customerEdit.company_id, stateInfo]);

  return (
    <>
      <Card className="mb-5">
        <Loading isLoading={isLoading} />
        <ErrorProcessor error={apiErrors} />
        <Card.Header>
          <h4 className="card-heading">
            Atualizar dados contratuais do {dataName}
          </h4>
        </Card.Header>
        <Card.Body>
          {dataLoaded ? (
            <Forms
              initialValues={formEditContract}
              validationSchema={validationSchemaContract}
              onSubmit={handleSubmitContract}
              fields={fieldsContract}
              btnSubmit={{
                variant: 'primary',
                type: 'submit',
                text: 'Atualizar',
              }}
            />
          ) : null}
        </Card.Body>
      </Card>
      {formEditContract.id && (
        <Card className="mb-5">
          <Card.Header>
            <h4 className="card-heading">Carregar arquivo do contrato</h4>
          </Card.Header>
          <Card.Body className="text-center">
            {formEditContract.contracFileUrl && (
              <div className="mb-2 d-flex justify-content-start">
                <Link
                  to={formEditContract.contracFileUrl}
                  title="Clique para abrir o contrato atual"
                  target="_blank"
                >
                  <FaFileAlt /> Abrir contrato atual
                </Link>
              </div>
            )}
            <Form onSubmit={handleSubmitFile}>
              <Dropzone
                onChange={handleFileChange}
                acceptDocument
                acceptImage={false}
              />
            </Form>
          </Card.Body>
          <Card.Footer>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmitFile}
              className="mt-2"
            >
              Carregar
            </Button>
          </Card.Footer>
        </Card>
      )}
    </>
  );
}

CustomerContract.propTypes = {
  customer: PropTypes.shape({}).isRequired,
  stateInfo: PropTypes.bool.isRequired,
  stateAction: PropTypes.func.isRequired,
};
